import { createSearchParams } from 'react-router';
import type { AiConfigFilter } from '@gonfalon/ai-configs/filters';
import { parseUIAiConfigFilterFromBrowser, serializeUIAiConfigFilterForBrowser } from '@gonfalon/ai-configs/filters';
import { makeEnvironmentAwareSearchParamsParser } from '@gonfalon/app-context/makeEnvironmentAwareSearchParamsParser';
import { makeEnvironmentAwareSearchParamsSerializer } from '@gonfalon/app-context/makeEnvironmentAwareSearchParamsSerializer';
import { createStorageSlot } from '@gonfalon/browser-storage/createStorageSlot';
import { parseNumber } from '@gonfalon/filters/parseNumber';
import type { GetAIConfigsQueryParams } from '@gonfalon/openapi/operations/getAIConfigs';
import { mergeSearchParams } from '@gonfalon/router/mergeSearchParams';
import { startOfDay, subDays } from 'date-fns';
import { isEqual } from 'lodash';
import { z } from 'zod';

export const AI_CONFIG_LIST_LIMIT = 20;
export const AI_CONFIG_LIST_INITIAL_OFFSET = 0;

export const AI_CONFIG_SORT_ORDER_CACHE_KEY = 'ai-config-list-sort-ordering';

export const AI_CONFIG_LIST_CACHE_KEY = 'ai-config-list-filters';

const sortOrderingSchema = z.enum(['creationDate', '-creationDate', 'name', '-name', 'lastModified', '-lastModified']);

export type SortOrdering = z.infer<typeof sortOrderingSchema>;

export const defaultSortOrdering = '-lastModified';

function assertSortParam(value: unknown): asserts value is SortOrdering {
  sortOrderingSchema.parse(value);
}

export function ensureSortParam(name: unknown) {
  assertSortParam(name);
  return name;
}

export function isDefaultSortOrdering(value: unknown): value is SortOrdering {
  return value === defaultSortOrdering;
}

function parseSort(value: unknown) {
  const result = sortOrderingSchema.safeParse(value);
  if (!result.success) {
    return undefined;
  }

  return result.data;
}

export type { AiConfigFilter };
export { parseUIAiConfigFilterFromBrowser, serializeUIAiConfigFilterForBrowser };

export type UIListViewFilter = Omit<GetAIConfigsQueryParams, 'filter'> & {
  filter?: AiConfigFilter;
};

type CreationDate = NonNullable<AiConfigFilter['created']>;

const aiConfigFilters = ['created', 'maintainerId', 'maintainerTeamKey', 'tags', 'view', 'mode'] as const;

export type AiConfigFilterSubset = Pick<AiConfigFilter, (typeof aiConfigFilters)[number]>;

const defaultAiConfigFilter: AiConfigFilter = {};

function isProjectFilter(value: string): value is keyof AiConfigFilter {
  return aiConfigFilters.some((filter) => filter === value);
}

export type FilterExcludeOptions = {
  excludeKeys?: Array<keyof AiConfigFilter>;
};

export function hasActiveFilters(filters: AiConfigFilter | undefined, options?: FilterExcludeOptions) {
  return countActiveProjectFilters(filters, options) > 0 || !!filters?.query;
}

function isDefaultAiConfigFilterValue<K extends keyof AiConfigFilter>(name: K, value: AiConfigFilter[K]) {
  if (value === undefined) {
    return false;
  }

  return isEqual(value, defaultAiConfigFilter[name]);
}

export function countActiveProjectFilters(filters: AiConfigFilter | undefined, options?: FilterExcludeOptions) {
  if (filters === undefined) {
    return 0;
  }

  const excludeKeys = options?.excludeKeys ?? [];
  let count = 0;

  for (const key of aiConfigFilters) {
    const value = filters[key];

    if (value === undefined) {
      continue;
    }

    if (isDefaultAiConfigFilterValue(key, value)) {
      continue;
    }

    // views has its own filter, so we don't count it as an active filter
    if (key === 'view') {
      continue;
    }

    if (excludeKeys.includes(key)) {
      continue;
    }

    if (typeof value === 'string') {
      if (value !== '') {
        count++;
        continue;
      }
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        count++;
        continue;
      }
    }

    count++;
  }

  return count;
}

export function parseUISortParamsFromBrowser(searchParams: URLSearchParams) {
  const ui: UIListViewFilter = {};
  ui.offset = parseNumber(searchParams.get('offset'));
  ui.sort = parseSort(searchParams.get('sort'));
  ui.filter = parseUIAiConfigFilterFromBrowser(searchParams);

  return ui;
}

export function serializeUISortParamsForBrowser(ui: UIListViewFilter) {
  let searchParams = new URLSearchParams();

  if ((ui.offset ?? 0) > 0) {
    searchParams.set('offset', String(ui.offset));
  }

  if (ui.sort) {
    searchParams.set('sort', ui.sort);
  }

  const filter = ui.filter;
  if (filter !== undefined) {
    const filterParams = serializeUIAiConfigFilterForBrowser(filter);
    searchParams = mergeSearchParams(searchParams, filterParams);
  }

  return searchParams;
}

export function clearProjectFilters(filter: AiConfigFilter, options?: FilterExcludeOptions) {
  const cleared = structuredClone(filter);
  const excludeKeys = options?.excludeKeys ?? [];

  for (const key of Object.keys(filter)) {
    if (isProjectFilter(key)) {
      if (excludeKeys.includes(key)) {
        continue;
      }
      cleared[key] = undefined;
    }
  }

  return cleared;
}

const slot = createStorageSlot(AI_CONFIG_SORT_ORDER_CACHE_KEY, {
  storageType: 'sessionStorage',
  isEnabled: true,
});

export function readCachedSortOrdering() {
  try {
    const raw = slot.get();
    return ensureSortParam(raw);
  } catch {
    slot.remove();
    return undefined;
  }
}

const cacheSlot = createStorageSlot(AI_CONFIG_LIST_CACHE_KEY, {
  storageType: 'sessionStorage',
  isEnabled: true,
});

export function readCachedAiConfigListFilters() {
  const raw = cacheSlot.get();

  if (!raw) {
    return {};
  }

  const params = createSearchParams(raw);
  return parseUIAiConfigFilterFromBrowser(params);
}

export function serializeUIAiConfigListViewForAPI(ui: UIListViewFilter) {
  const api: GetAIConfigsQueryParams = {};
  api.limit = ui.limit;
  api.offset = ui.offset;
  api.sort = ui.sort;

  const uiFilter = ui.filter;
  if (uiFilter !== undefined) {
    const apiFilter: NonNullable<GetAIConfigsQueryParams['filter']> = {};

    apiFilter.query = uiFilter.query;
    apiFilter.tags = uiFilter.tags;
    apiFilter.maintainerId = uiFilter.maintainerId;
    apiFilter.maintainerTeamKey = uiFilter.maintainerTeamKey;
    apiFilter.view = uiFilter.view;
    apiFilter.mode = uiFilter.mode;

    if (uiFilter.created !== undefined) {
      const now = Date.now();
      const createdDate = creationDateRangeToDate(uiFilter.created, now);
      apiFilter.creationDate = createdDate ? { before: createdDate.getTime() } : undefined;
    }

    api.filter = apiFilter;
  }

  return api;
}

function creationDateRangeToDate(creationDate: CreationDate, referenceTimestamp: number) {
  const today = startOfDay(referenceTimestamp);

  switch (creationDate) {
    case '1-week-ago':
      return subDays(today, 7);
    case '1-month-ago':
      return subDays(today, 30);
    case '2-months-ago':
      return subDays(today, 60);
    case '3-months-ago':
      return subDays(today, 90);
    case '6-months-ago':
      return subDays(today, 180);
    case '1-year-ago':
      return subDays(today, 365);
    default:
      return undefined;
  }
}

export const parseEnvironmentAwareUISortParamsFromBrowser =
  makeEnvironmentAwareSearchParamsParser(parseUISortParamsFromBrowser);

export const serializeEnvironmentAwareUISortParamsForBrowser = makeEnvironmentAwareSearchParamsSerializer(
  serializeUISortParamsForBrowser,
);
